import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * RealtimeContext — one shared Server-Sent Events connection per logged-in
 * session (backend: GET /api/events/stream, backend/events_service.py).
 *
 * Any component can read `lastNotification` (bumps on every event received,
 * with a fresh object reference each time) to react instantly instead of
 * waiting for its next 60s poll — e.g. BellDropdown, useNavBadges, or a
 * payments page that wants to refetch as soon as a tenant pays.
 *
 * `unreadCount` also lives here (not in BellDropdown): AppLayout renders
 * BellDropdown TWICE at once (desktop sidebar + mobile top bar, toggled
 * by CSS breakpoint, both mounted), so each kept its own 60s poll and its
 * own GET /notifications/unread-count on load -- two identical requests
 * on every page, on every app. Centralizing it here means ONE poll/fetch
 * shared by both.
 */
const RealtimeContext = createContext({ lastNotification: null, unreadCount: 0, refreshUnreadCount: () => {} });

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const [lastNotification, setLastNotification] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  // Con contraseña temporal pendiente el backend responde 403: no abrir SSE ni poll.
  const blocked = !!user?.must_change_password;
  const esRef = useRef(null);
  const retryRef = useRef(1000);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;
    try { const { data } = await api.get("/notifications/unread-count"); setUnreadCount(data.count); } catch { /* red/token: se reintenta en el siguiente poll */ }
  }, [user]);

  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    if (blocked) return;
    refreshUnreadCount();
    const id = setInterval(refreshUnreadCount, 60_000);
    return () => clearInterval(id);
  }, [user, blocked, refreshUnreadCount]);

  // Actualización instantánea ante cualquier notificación en vivo (SSE),
  // en vez de esperar al poll de 60 s.
  useEffect(() => { if (lastNotification) refreshUnreadCount(); }, [lastNotification, refreshUnreadCount]);

  useEffect(() => {
    // Only keep a connection open for an authenticated session.
    if (!user || blocked) {
      esRef.current?.close();
      esRef.current = null;
      return;
    }

    let stopped = false;
    let reconnectTimer = null;
    let fetchingTicket = false; // evita dos conexiones a la vez (foco + reintento)
    let evicted = false; // el servidor cerró esta conexión por exceso de pestañas

    const retryLater = () => {
      if (stopped || evicted) return;
      reconnectTimer = setTimeout(connect, retryRef.current);
      retryRef.current = Math.min(retryRef.current * 2, 30000);
    };

    const connect = async () => {
      if (stopped) return;
      clearTimeout(reconnectTimer);
      // EventSource no puede mandar cabeceras: en vez del JWT en la URL se pide
      // con la sesión normal un ticket de un solo uso (caduca en 60 s).
      if (fetchingTicket) return;
      fetchingTicket = true;
      let ticket;
      try {
        ({ data: { ticket } } = await api.post("/events/ticket"));
      } catch {
        fetchingTicket = false;
        retryLater();
        return;
      }
      fetchingTicket = false;
      if (stopped) return;
      esRef.current?.close();
      const url = `${API_BASE}/events/stream?ticket=${encodeURIComponent(ticket)}`;
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      es.addEventListener("notification", (e) => {
        try {
          const doc = JSON.parse(e.data);
          setLastNotification({ ...doc, _receivedAt: Date.now() });
          if (!doc.read) {
            toast(doc.title, { description: doc.body });
          }
        } catch {
          /* ignore malformed event */
        }
      });

      // El servidor cierra las conexiones más antiguas cuando el usuario abre
      // demasiadas pestañas: esta no reconecta sola (expulsaría a otra en
      // bucle); vuelve a conectar cuando el usuario regrese a la pestaña.
      es.addEventListener("_closed", () => {
        evicted = true;
        es.close();
      });

      es.onopen = () => { retryRef.current = 1000; };

      es.onerror = () => {
        es.close();
        // Reintento con espera creciente y un ticket nuevo (el anterior ya se usó).
        retryLater();
      };
    };

    // Mobile/desktop browsers frequently kill an idle EventSource when the
    // tab is backgrounded or the device sleeps/loses network — often
    // silently, without ever firing `onerror`, so `connect()`'s own retry
    // logic never kicks in. Without this, the stream can stay dead
    // indefinitely and the only way to get live updates back is a full
    // page reload. Force a fresh connection whenever the tab/window comes
    // back to the foreground or the network comes back, unless the
    // existing connection is already open (readyState 1).
    const forceReconnectIfStale = () => {
      if (stopped) return;
      if (esRef.current && esRef.current.readyState === EventSource.OPEN) return;
      evicted = false;
      esRef.current?.close();
      retryRef.current = 1000;
      connect();
    };
    const onVisibilityChange = () => { if (document.visibilityState === "visible") forceReconnectIfStale(); };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", forceReconnectIfStale);
    window.addEventListener("online", forceReconnectIfStale);

    connect();

    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", forceReconnectIfStale);
      window.removeEventListener("online", forceReconnectIfStale);
      esRef.current?.close();
      esRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, blocked]);

  return (
    <RealtimeContext.Provider value={{ lastNotification, unreadCount, refreshUnreadCount }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  return useContext(RealtimeContext);
}

/**
 * Convenience hook: runs `onNotification(doc)` whenever a new push arrives
 * whose `type` starts with any of `prefixes` (e.g. ["owner.payment",
 * "owner.bizum"]). Pass no prefixes to react to every notification.
 */
export function useLiveNotification(onNotification, prefixes = null) {
  const { lastNotification } = useRealtime();
  // Ref so the effect always calls the *latest* callback/prefixes (e.g. a
  // BellDropdown reading its current `open` state) without having to
  // re-subscribe every render — it only needs to fire on new events.
  const cbRef = useRef(onNotification);
  cbRef.current = onNotification;
  const prefixesRef = useRef(prefixes);
  prefixesRef.current = prefixes;

  useEffect(() => {
    if (!lastNotification) return;
    const p = prefixesRef.current;
    if (p && !p.some((pre) => (lastNotification.type || "").startsWith(pre))) return;
    cbRef.current(lastNotification);
  }, [lastNotification]);
}
